import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { ArrowCheckIcon } from '@sharedCR/coreDesignSystem/icons';
import { aiExpressionAuthoringApi } from 'app/services/api';
import ExplainMarkdownText from 'app/shared/components/form/ExpressionField/ExplainExpression/ExplainMarkdownText';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';

import { GenerateExpressionTestIds } from '../GenerateExpression.testIds';
import { isCancelledError } from '../GenerateExpression.utils';

import {
  PREVIEW_COLLAPSE_ALL_LABEL,
  PREVIEW_EXPAND_ALL_LABEL,
  PREVIEW_EXPLAIN_BUTTON_LABEL,
  PREVIEW_EXPLAIN_ERROR_HELPER,
  PREVIEW_EXPLAIN_HIDE_BUTTON_LABEL,
  PREVIEW_EXPLAIN_LOADING_HELPER,
  PREVIEW_GROUP_COUNT_SUFFIX_PLURAL,
  PREVIEW_GROUP_COUNT_SUFFIX_SINGULAR,
  PREVIEW_GROUP_OPERATOR_DESCRIPTION,
  PREVIEW_GROUP_OPERATOR_PILL_LABEL,
  PREVIEW_LEAF_UNRENDERABLE_LABEL,
  PREVIEW_NODE_KIND,
  PREVIEW_STATUS_BADGE_LABEL,
  PREVIEW_STATUS_GLYPH,
} from './ExpressionPreview.constants';
import {
  buildGroupChildrenWrapperStyles,
  buildGroupOperatorPillStyles,
  buildLeafGlyphStyles,
  buildLeafStatusBadgeStyles,
  buildLeafTextStyles,
  expressionPreviewExpandToggleStyles,
  expressionPreviewHeaderRowStyles,
  expressionPreviewHeaderSpacerStyles,
  expressionPreviewWrapperStyles,
  groupChevronStyles,
  groupHeaderRowStyles,
  groupHeaderSummaryRowStyles,
  groupNodeWrapperStyles,
  leafActionsRowStyles,
  leafExplainButtonStyles,
  leafExplainPanelStyles,
  leafExplainStatusRowStyles,
  leafFieldPathStyles,
  leafMainRowStyles,
  leafRowStyles,
  previewEmptyHelperStyles,
} from './ExpressionPreview.styles';
import {
  buildPreviewNodeFromAst,
  buildUnifiedDiff,
  collectAllGroupNodeIds,
  collectLeafCount,
  resolveDefaultCollapsedNodeIds,
  sortPreviewTree,
} from './ExpressionPreview.utils';

const EXPLAIN_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

const ROOT_NODE_ID = 'root';

const formatLeafCount = leafCount => {
  const suffix =
    leafCount === 1 ? PREVIEW_GROUP_COUNT_SUFFIX_SINGULAR : PREVIEW_GROUP_COUNT_SUFFIX_PLURAL;

  return `${leafCount} ${suffix}`;
};

const PreviewLeaf = ({ node, schemaDomain, schemaJson, isExplainDisabled }) => {
  const [explainStatus, setExplainStatus] = useState(EXPLAIN_STATUS.IDLE);
  const [explainResult, setExplainResult] = useState(null);
  const abortControllerRef = useRef(null);

  const cancelInflight = useCallback(() => {
    if (_.isNil(abortControllerRef.current)) {
      return;
    }

    abortControllerRef.current.abort();
    abortControllerRef.current = null;
  }, []);

  useEffect(() => () => cancelInflight(), [cancelInflight]);

  const onExplainClick = useCallback(() => {
    if (explainStatus === EXPLAIN_STATUS.LOADING) {
      return;
    }

    if (explainStatus === EXPLAIN_STATUS.SUCCESS || explainStatus === EXPLAIN_STATUS.ERROR) {
      cancelInflight();
      setExplainStatus(EXPLAIN_STATUS.IDLE);
      setExplainResult(null);

      return;
    }

    cancelInflight();

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setExplainStatus(EXPLAIN_STATUS.LOADING);
    setExplainResult(null);

    aiExpressionAuthoringApi
      .explainExpression({
        schemaDomain,
        expressionJson: node.expressionJson,
        expressionText: node.text,
        schemaJson,
        signal: controller.signal,
      })
      .then(({ data }) => {
        if (controller.signal.aborted) {
          return;
        }

        setExplainResult(data);
        setExplainStatus(EXPLAIN_STATUS.SUCCESS);
      })
      .catch(error => {
        if (isCancelledError(error) || controller.signal.aborted) {
          return;
        }

        setExplainStatus(EXPLAIN_STATUS.ERROR);
      });
  }, [cancelInflight, explainStatus, node.expressionJson, node.text, schemaDomain, schemaJson]);

  const hasText = !_.isEmpty(_.trim(node.text));
  const hasFieldPaths = !_.isEmpty(node.fieldPaths);
  const badgeLabel = PREVIEW_STATUS_BADGE_LABEL[node.status] ?? null;
  const isExplainPanelVisible =
    explainStatus === EXPLAIN_STATUS.LOADING ||
    explainStatus === EXPLAIN_STATUS.SUCCESS ||
    explainStatus === EXPLAIN_STATUS.ERROR;
  const explainButtonLabel =
    explainStatus === EXPLAIN_STATUS.SUCCESS || explainStatus === EXPLAIN_STATUS.ERROR
      ? PREVIEW_EXPLAIN_HIDE_BUTTON_LABEL
      : PREVIEW_EXPLAIN_BUTTON_LABEL;
  const isExplainButtonDisabled =
    isExplainDisabled || _.isEmpty(node.expressionJson) || explainStatus === EXPLAIN_STATUS.LOADING;

  return (
    <Box data-testid={GenerateExpressionTestIds.MODAL_PREVIEW_TREE_LEAF} sx={leafRowStyles}>
      <Box sx={leafMainRowStyles}>
        <Box aria-hidden="true" sx={buildLeafGlyphStyles(node.status)}>
          {PREVIEW_STATUS_GLYPH[node.status] ?? PREVIEW_STATUS_GLYPH.unchanged}
        </Box>
        {!_.isNil(badgeLabel) && (
          <Typography
            data-testid={GenerateExpressionTestIds.MODAL_PREVIEW_TREE_LEAF_STATUS_BADGE}
            sx={buildLeafStatusBadgeStyles(node.status)}
            variant="bold12"
          >
            {badgeLabel ?? ''}
          </Typography>
        )}
        <Typography
          component="div"
          data-testid={GenerateExpressionTestIds.MODAL_PREVIEW_TREE_LEAF_TEXT}
          sx={buildLeafTextStyles(node.status)}
          variant="regular14"
        >
          {hasText ? node.text : PREVIEW_LEAF_UNRENDERABLE_LABEL}
        </Typography>
      </Box>

      {hasFieldPaths && (
        <Box
          component="code"
          data-testid={GenerateExpressionTestIds.MODAL_PREVIEW_TREE_LEAF_FIELD_PATHS}
          sx={leafFieldPathStyles}
        >
          {node.fieldPaths.join(', ')}
        </Box>
      )}

      <Box sx={leafActionsRowStyles}>
        <Button
          color="primary"
          data-testid={GenerateExpressionTestIds.MODAL_PREVIEW_TREE_LEAF_EXPLAIN_BUTTON}
          disabled={isExplainButtonDisabled}
          sx={leafExplainButtonStyles}
          variant="text"
          onClick={onExplainClick}
        >
          {explainButtonLabel}
        </Button>
      </Box>

      {isExplainPanelVisible && (
        <Box
          data-testid={GenerateExpressionTestIds.MODAL_PREVIEW_TREE_LEAF_EXPLAIN_PANEL}
          sx={leafExplainPanelStyles}
        >
          {explainStatus === EXPLAIN_STATUS.LOADING && (
            <Box sx={leafExplainStatusRowStyles}>
              <CircularProgress size={14} />
              <Typography color="text.secondary" variant="regular14">
                {PREVIEW_EXPLAIN_LOADING_HELPER}
              </Typography>
            </Box>
          )}

          {explainStatus === EXPLAIN_STATUS.ERROR && (
            <Typography color="error.main" variant="regular14">
              {PREVIEW_EXPLAIN_ERROR_HELPER}
            </Typography>
          )}

          {explainStatus === EXPLAIN_STATUS.SUCCESS && (
            <Typography component="div" variant="regular14">
              <ExplainMarkdownText text={explainResult?.summary} />
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

PreviewLeaf.propTypes = {
  node: PropTypes.shape({
    fieldPaths: PropTypes.arrayOf(PropTypes.string).isRequired,
    status: PropTypes.string.isRequired,
    text: PropTypes.string,
    expressionJson: PropTypes.string,
  }).isRequired,
  schemaDomain: PropTypes.string.isRequired,
  isExplainDisabled: PropTypes.bool.isRequired,
  schemaJson: PropTypes.string,
};

const PreviewGroup = ({
  node,
  nodeId,
  collapsedGroupIds,
  onToggleGroup,
  schemaDomain,
  schemaJson,
  isExplainDisabled,
}) => {
  const isCollapsed = collapsedGroupIds.has(nodeId);
  const leafCount = useMemo(() => collectLeafCount(node), [node]);
  const operatorPillLabel = PREVIEW_GROUP_OPERATOR_PILL_LABEL[node.operator];
  const operatorDescription = PREVIEW_GROUP_OPERATOR_DESCRIPTION[node.operator];

  const onHeaderClick = useCallback(() => onToggleGroup(nodeId), [nodeId, onToggleGroup]);
  const onHeaderKeyDown = useCallback(
    event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggleGroup(nodeId);
      }
    },
    [nodeId, onToggleGroup],
  );

  const chevronDirection = isCollapsed ? 'right' : 'down';

  return (
    <Box
      data-testid={GenerateExpressionTestIds.MODAL_PREVIEW_TREE_GROUP}
      sx={groupNodeWrapperStyles}
    >
      <Box
        aria-expanded={!isCollapsed}
        data-testid={GenerateExpressionTestIds.MODAL_PREVIEW_TREE_GROUP_HEADER}
        role="button"
        sx={groupHeaderRowStyles}
        tabIndex={0}
        onClick={onHeaderClick}
        onKeyDown={onHeaderKeyDown}
      >
        <Box aria-hidden="true" sx={groupChevronStyles}>
          <ArrowCheckIcon direction={chevronDirection} size={IconSize[20]} />
        </Box>
        <Box sx={groupHeaderSummaryRowStyles}>
          <Typography sx={buildGroupOperatorPillStyles(node.operator)} variant="bold12">
            {operatorPillLabel}
          </Typography>
          <Typography color="text.secondary" variant="regular14">
            {formatLeafCount(leafCount)}
          </Typography>
          <Typography color="text.secondary" variant="regular14">
            {operatorDescription}
          </Typography>
        </Box>
      </Box>

      <Box
        data-testid={GenerateExpressionTestIds.MODAL_PREVIEW_TREE_GROUP_CHILDREN}
        sx={buildGroupChildrenWrapperStyles(node.operator, isCollapsed)}
      >
        {node.children.map((child, index) => {
          const childNodeId = `${nodeId}/${index}`;

          if (child.kind === PREVIEW_NODE_KIND.GROUP) {
            return (
              <PreviewGroup
                collapsedGroupIds={collapsedGroupIds}
                isExplainDisabled={isExplainDisabled}
                key={childNodeId}
                node={child}
                nodeId={childNodeId}
                schemaDomain={schemaDomain}
                schemaJson={schemaJson}
                onToggleGroup={onToggleGroup}
              />
            );
          }

          return (
            <PreviewLeaf
              isExplainDisabled={isExplainDisabled}
              key={childNodeId}
              node={child}
              schemaDomain={schemaDomain}
              schemaJson={schemaJson}
            />
          );
        })}
      </Box>
    </Box>
  );
};

PreviewGroup.propTypes = {
  node: PropTypes.shape({
    operator: PropTypes.string.isRequired,
    children: PropTypes.array.isRequired,
  }).isRequired,
  nodeId: PropTypes.string.isRequired,
  collapsedGroupIds: PropTypes.instanceOf(Set).isRequired,
  schemaDomain: PropTypes.string.isRequired,
  isExplainDisabled: PropTypes.bool.isRequired,
  onToggleGroup: PropTypes.func.isRequired,
  schemaJson: PropTypes.string,
};

const ExpressionPreview = ({
  previousAst = null,
  proposedAst,
  schemaDomain,
  schemaJson = null,
  isExplainDisabled = false,
  isNewExpressionsParsingLogicEnabled,
  emptyHelperText,
}) => {
  const sortedDiff = useMemo(() => {
    const previousRoot = buildPreviewNodeFromAst(previousAst, {
      isNewExpressionsParsingLogicEnabled,
    });
    const proposedRoot = buildPreviewNodeFromAst(proposedAst, {
      isNewExpressionsParsingLogicEnabled,
    });
    const merged = buildUnifiedDiff({ previousRoot, proposedRoot });

    return sortPreviewTree(merged);
  }, [previousAst, proposedAst, isNewExpressionsParsingLogicEnabled]);

  const [collapsedGroupIds, setCollapsedGroupIds] = useState(() =>
    resolveDefaultCollapsedNodeIds(sortedDiff),
  );

  const previousDiffRef = useRef(sortedDiff);
  useEffect(() => {
    if (previousDiffRef.current !== sortedDiff) {
      previousDiffRef.current = sortedDiff;
      setCollapsedGroupIds(resolveDefaultCollapsedNodeIds(sortedDiff));
    }
  }, [sortedDiff]);

  const onToggleGroup = useCallback(nodeId => {
    setCollapsedGroupIds(current => {
      const next = new Set(current);

      if (next.has(nodeId)) {
        next.delete(nodeId);

        return next;
      }

      next.add(nodeId);

      return next;
    });
  }, []);

  const allGroupNodeIds = useMemo(() => collectAllGroupNodeIds(sortedDiff), [sortedDiff]);
  const hasGroupNodes = allGroupNodeIds.size > 0;
  const isFullyExpanded = collapsedGroupIds.size === 0;
  const expandAllLabel = isFullyExpanded ? PREVIEW_COLLAPSE_ALL_LABEL : PREVIEW_EXPAND_ALL_LABEL;

  const onToggleExpandAll = useCallback(() => {
    setCollapsedGroupIds(current => (current.size === 0 ? new Set(allGroupNodeIds) : new Set()));
  }, [allGroupNodeIds]);

  if (_.isNil(sortedDiff)) {
    return (
      <Box data-testid={GenerateExpressionTestIds.MODAL_PREVIEW_TREE} sx={previewEmptyHelperStyles}>
        <Typography color="text.secondary" component="div" variant="regular14">
          {emptyHelperText}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      data-testid={GenerateExpressionTestIds.MODAL_PREVIEW_TREE}
      sx={expressionPreviewWrapperStyles}
    >
      <Box sx={expressionPreviewHeaderRowStyles}>
        <Box sx={expressionPreviewHeaderSpacerStyles} />
        <Button
          color="primary"
          data-testid={GenerateExpressionTestIds.MODAL_PREVIEW_TREE_EXPAND_ALL_TOGGLE}
          isHidden={!hasGroupNodes}
          sx={expressionPreviewExpandToggleStyles}
          variant="text"
          onClick={onToggleExpandAll}
        >
          {expandAllLabel}
        </Button>
      </Box>

      {sortedDiff.kind === PREVIEW_NODE_KIND.GROUP ? (
        <PreviewGroup
          collapsedGroupIds={collapsedGroupIds}
          isExplainDisabled={isExplainDisabled}
          node={sortedDiff}
          nodeId={ROOT_NODE_ID}
          schemaDomain={schemaDomain}
          schemaJson={schemaJson}
          onToggleGroup={onToggleGroup}
        />
      ) : (
        <PreviewLeaf
          isExplainDisabled={isExplainDisabled}
          node={sortedDiff}
          schemaDomain={schemaDomain}
          schemaJson={schemaJson}
        />
      )}
    </Box>
  );
};

ExpressionPreview.propTypes = {
  schemaDomain: PropTypes.string.isRequired,
  isNewExpressionsParsingLogicEnabled: PropTypes.bool.isRequired,
  emptyHelperText: PropTypes.string.isRequired,
  proposedAst: PropTypes.object,
  previousAst: PropTypes.object,
  schemaJson: PropTypes.string,
  isExplainDisabled: PropTypes.bool,
};

export default ExpressionPreview;
